import { HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  IELAsyncAutocompleteElement,
  SELECTION_MODE,
} from '@el-async-autocomplete';

import {
  SUBMISSION_REFERENCE_CATEGORIES,
  SUBMISSION_REFERENCE_CATEGORY_FIELD,
  SUBMISSION_REFERENCE_SECTION,
  URL_NO_TRAILING_SLASH_REGEX,
} from '@shared/constants';
import {
  IIdentity,
  IInvitees,
  IReferenceCategoryResponse,
} from '@shared/interfaces';
import { getArrangedName } from '@shared/utils';

import { IdentitiesService } from '../../../../../identities/services/identities.service';
import {
  ReferenceCategoryService,
  ReferenceService,
} from '../../../../../references/services';
import { IFilterData } from '../../../../../setup/messages/popups/add-new-message-group/add-new-group.component';
import { IArrangeTechnicalInterviewPopupOutput } from '../arrange-technical-interview-popup/arrange-technical-interview-popup.component';

export enum SCHEDULE_TYPES {
  REASSIGN = 'REASSIGN',
  RESCHEDULE = 'RESCHEDULE',
}

const MEETING_OPTIONS = {
  PHYSICALLY: 'Physically',
  ONLINE: 'Online',
};

@Component({
  selector: 'app-reassign-and-reschedule-interview-popup',
  templateUrl: './reassign-and-reschedule-interview-popup.component.html',
  styleUrls: ['./reassign-and-reschedule-interview-popup.component.scss'],
})
export class ReassignAndRescheduleInterviewPopupComponent implements OnInit {
  minDate = new Date();
  meetingType: string;
  readonly MEETING_OPTIONS = MEETING_OPTIONS;
  readonly meetingOptions = [
    { type: MEETING_OPTIONS.PHYSICALLY, checked: true },
    { type: MEETING_OPTIONS.ONLINE, checked: false },
  ];
  constructor(
    private dialogRef: MatDialogRef<ReassignAndRescheduleInterviewPopupComponent>,
    private identitiesService: IdentitiesService,
    private referenceCategoryService: ReferenceCategoryService,
    private referenceService: ReferenceService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      invitees: IIdentity[];
      scheduleType: SCHEDULE_TYPES;
    }
  ) {}

  invitees: IELAsyncAutocompleteElement[] = [];
  filteredInvitees: IInvitees[] = [];
  selectedUsers: IELAsyncAutocompleteElement[] = [];

  SELECTION_MODE = SELECTION_MODE;

  arrangeTechnicalInterviewFormGroup = new FormGroup({
    date: new FormControl(null, Validators.required),
    from: new FormControl(null, Validators.required),
    to: new FormControl(null, [Validators.required, this.checkToDateValidator]),
    location: new FormControl('', Validators.required),
    meeting_type: new FormControl(''),
    meeting_link: new FormControl('', [
      Validators.pattern(URL_NO_TRAILING_SLASH_REGEX),
    ]),
  });

  locations: string[];
  referenceCategories: IReferenceCategoryResponse[];

  ngOnInit(): void {
    this.listenToReferences();

    this.updateValidators();

    this.arrangeTechnicalInterviewFormGroup.controls.meeting_type.setValue(
      'Physically'
    );
    this.arrangeTechnicalInterviewFormGroup.controls.meeting_type?.valueChanges.subscribe(
      () => {
        this.updateValidators();
      }
    );

    this.data.invitees.forEach((invitee) => {
      this.selectedUsers.push({
        key: invitee._id,
        value: getArrangedName({
          salutation: invitee.salutation,
          first_name: invitee.first_name,
          last_name: invitee.last_name,
        }),
        disable: true,
      });
    });
  }

  updateValidators(): void {
    this.arrangeTechnicalInterviewFormGroup.controls.location.reset();
    this.arrangeTechnicalInterviewFormGroup.controls.meeting_link.reset();
    if (this.meetingType === this.MEETING_OPTIONS.ONLINE) {
      // Meeting link is required and must match the pattern
      this.arrangeTechnicalInterviewFormGroup.controls.meeting_link.setValidators(
        [Validators.required]
      );
      this.arrangeTechnicalInterviewFormGroup.controls.location.clearValidators();
    } else {
      // Location is required; meeting link is optional
      this.arrangeTechnicalInterviewFormGroup.controls.location.setValidators(
        Validators.required
      );
      this.arrangeTechnicalInterviewFormGroup.controls.meeting_link.clearValidators();
    }

    this.arrangeTechnicalInterviewFormGroup.controls.location.updateValueAndValidity();
    this.arrangeTechnicalInterviewFormGroup.controls.meeting_link.updateValueAndValidity();
  }

  onMeetingTypeChange(type: string): void {
    this.meetingType = type;
    this.arrangeTechnicalInterviewFormGroup.controls.meeting_type.setValue(
      type
    );
    this.updateValidators();
  }

  onClickClose() {
    const data: IArrangeTechnicalInterviewPopupOutput = {
      date: this.arrangeTechnicalInterviewFormGroup.controls.date.value,
      from: this.arrangeTechnicalInterviewFormGroup.controls.from.value,
      to: this.arrangeTechnicalInterviewFormGroup.controls.to.value,
      meeting_type:
        this.arrangeTechnicalInterviewFormGroup.controls.meeting_type.value,
      location:
        this.arrangeTechnicalInterviewFormGroup.controls?.location?.value,
      meeting_link:
        this.arrangeTechnicalInterviewFormGroup.controls?.meeting_link?.value,
    };
    this.dialogRef.close(data);
  }

  onFilterUsers(eventData: IFilterData) {
    if (eventData.type === 'scroll') {
      let httpParams = new HttpParams();
      httpParams = httpParams.append('name', eventData.event);
      httpParams = httpParams.append('start', eventData.start);
      httpParams = httpParams.append('size', eventData.size);
      this.identitiesService
        .getIdentitiesWithoutContactPerson(httpParams)
        .subscribe((data_all_users) => {
          const filterUserList = [];
          data_all_users.data.forEach((user) => {
            filterUserList.push({
              key: user._id,
              value: `${user?.first_name} ${
                user?.last_name ? user?.last_name : ''
              } `,
            });
          });

          this.invitees = this.invitees.concat(filterUserList);
        });
    } else if (eventData.type === 'typing') {
      let httpParams = new HttpParams();
      httpParams = httpParams.append('name', eventData.event);
      httpParams = httpParams.append('start', eventData.start);
      httpParams = httpParams.append('size', eventData.size);
      this.identitiesService
        .getIdentitiesWithoutContactPerson(httpParams)
        .subscribe((data_all_users) => {
          const filterUserList = [];
          data_all_users.data.forEach((user) => {
            filterUserList.push({
              key: user._id,
              value: `${user?.first_name} ${
                user?.last_name ? user?.last_name : ''
              } `,
            });
          });

          this.invitees = filterUserList;
        });
    }
  }

  selectUser(e) {
    this.filteredInvitees = [];
    e.forEach((element) => {
      this.filteredInvitees.push({
        id: element.key,
        name: element.value,
      });
    });
  }

  checkToDateValidator(control) {
    const fromDate = new Date(control.parent?.get('from').value);
    const toDate = new Date(control.value);
    if (toDate <= fromDate) {
      return { invalidToDate: true };
    }
    return null;
  }

  listenToReferences() {
    // this.isLoading = true;
    this.referenceCategoryService.allCategories.subscribe(async (data) => {
      if (data && data.length === 0) {
        await this.referenceCategoryService.getAllReferenceCategories();
      }
      if (
        this.referenceCategories?.length === 0 ||
        this.referenceCategories === undefined
      ) {
        this.referenceCategories = data;
        const categoriesId = this.referenceCategories.find((category) => {
          return (
            SUBMISSION_REFERENCE_SECTION.SUBMISSION.toUpperCase() ===
              category.section.toUpperCase() &&
            category?.name?.trim()?.toUpperCase() ===
              SUBMISSION_REFERENCE_CATEGORIES.LOCATIONS.toUpperCase()
          );
        })?._id;
        if (categoriesId) {
          const availableResourcesId = this.referenceCategories.find(
            (category) =>
              SUBMISSION_REFERENCE_SECTION.SUBMISSION.toUpperCase() ===
                category.section.toUpperCase() &&
              category?.name?.trim()?.toUpperCase() ===
                SUBMISSION_REFERENCE_CATEGORIES.LOCATIONS.toUpperCase()
          )?._id;
          if (availableResourcesId) {
            await this.referenceService
              .getReferences(availableResourcesId?.toString())
              .then((data) => {
                this.locations = [];
                data?.forEach((data) => {
                  data.reference?.forEach((ref) => {
                    if (
                      ref.field.name.toUpperCase() ===
                        SUBMISSION_REFERENCE_CATEGORY_FIELD.NAME.toUpperCase() &&
                      !data?.is_deleted
                    ) {
                      this.locations.push(data.reference[0].value.toString());
                      // this.isLoading = false;
                    } else {
                      // this.isLoading = false;
                    }
                  });
                });
              });
          }
        }
      }
    });
  }
}
